
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/user';

export async function getAllProfiles(): Promise<User[]> {
  try {
    // Fetch platform admins
    const { data: platformAdmins, error: platformAdminsError } = await supabase
      .from('platform_admins')
      .select('*')
      .order('created_at', { ascending: false });

    if (platformAdminsError) {
      console.error('Error fetching platform admins:', platformAdminsError);
    }

    // Fetch organization members
    const { data: orgMembers, error: orgMembersError } = await supabase
      .from('organization_members')
      .select(`
        *,
        organizations!inner(name)
      `)
      .order('created_at', { ascending: false });

    if (orgMembersError) {
      console.error('Error fetching organization members:', orgMembersError);
    }

    // Combine and format the data
    const allUsers: User[] = [];

    // Add platform admins
    if (platformAdmins) {
      platformAdmins.forEach(admin => {
        allUsers.push({
          id: admin.id,
          name: admin.full_name || admin.email,
          email: admin.email,
          role: 'Admin' as const,
          status: admin.is_active ? 'Active' : 'Inactive',
          lastActive: admin.updated_at,
          invitationStatus: admin.invitation_status as 'pending' | 'accepted' | 'expired',
          invitedAt: admin.invited_at,
          invitedBy: admin.invited_by,
          user_type: 'platform_admin' as const,
          organization_id: undefined,
          organization_name: undefined
        });
      });
    }

    // Add organization members
    if (orgMembers) {
      orgMembers.forEach((member: any) => {
        allUsers.push({
          id: member.id,
          name: member.full_name || member.email,
          email: member.email,
          role: (member.role.charAt(0).toUpperCase() + member.role.slice(1)) as 'Admin' | 'Editor' | 'Viewer',
          status: member.is_active ? 'Active' : 'Inactive',
          lastActive: member.updated_at,
          invitationStatus: member.invitation_status as 'pending' | 'accepted' | 'expired',
          invitedAt: member.invited_at,
          invitedBy: member.invited_by,
          user_type: member.user_type as 'org_admin' | 'team_member',
          organization_id: member.organization_id,
          organization_name: member.organizations?.name
        });
      });
    }

    return allUsers;
  } catch (error) {
    console.error('Error in getAllProfiles:', error);
    throw error;
  }
}

// Service-role based profile fetching to bypass RLS infinite recursion
export async function getProfile(userId: string) {
  console.log('getProfile called for userId:', userId);
  
  try {
    // Use service client to bypass RLS policies that cause infinite recursion
    const { createServiceClient } = await import('./migration/serviceClient');
    const serviceClient = createServiceClient();
    
    // First try the profiles table using service role
    const { data: profile, error: profileError } = await serviceClient
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    console.log('Service profile query result:', { profile, profileError });

    if (profile) {
      return {
        id: profile.id,
        email: profile.email,
        full_name: profile.full_name,
        role: profile.role,
        user_type: profile.user_type,
        organization_id: profile.organization_id,
        is_active: profile.is_active,
        invitation_status: profile.invitation_status,
        invited_at: profile.invited_at,
        invited_by: profile.invited_by,
        created_at: profile.created_at,
        updated_at: profile.updated_at
      };
    }

    // If not found in profiles, try platform_admins with service role
    const { data: platformAdmin, error: platformError } = await serviceClient
      .from('platform_admins')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    console.log('Service platform admin query result:', { platformAdmin, platformError });

    if (platformAdmin) {
      return {
        id: platformAdmin.id,
        email: platformAdmin.email,
        full_name: platformAdmin.full_name,
        role: 'admin',
        user_type: 'platform_admin',
        organization_id: null,
        is_active: platformAdmin.is_active,
        invitation_status: platformAdmin.invitation_status,
        invited_at: platformAdmin.invited_at,
        invited_by: platformAdmin.invited_by,
        created_at: platformAdmin.created_at,
        updated_at: platformAdmin.updated_at
      };
    }

    // Finally try organization_members with service role
    const { data: orgMember, error: orgError } = await serviceClient
      .from('organization_members')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    console.log('Service org member query result:', { orgMember, orgError });

    if (orgMember) {
      return {
        id: orgMember.id,
        email: orgMember.email,
        full_name: orgMember.full_name,
        role: orgMember.role,
        user_type: orgMember.user_type,
        organization_id: orgMember.organization_id,
        is_active: orgMember.is_active,
        invitation_status: orgMember.invitation_status,
        invited_at: orgMember.invited_at,
        invited_by: orgMember.invited_by,
        created_at: orgMember.created_at,
        updated_at: orgMember.updated_at
      };
    }

    // If not found in any table, throw error
    console.log('Profile not found in any table for userId:', userId);
    throw new Error('Profile not found');
  } catch (error) {
    console.error('Error in getProfile:', error);
    throw error;
  }
}

export async function updateProfile(userId: string, updates: any) {
  // First try platform_admins
  const { data: platformAdmin } = await supabase
    .from('platform_admins')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (platformAdmin) {
    const { data, error } = await supabase
      .from('platform_admins')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Then try organization_members
  const { data, error } = await supabase
    .from('organization_members')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteProfile(userId: string) {
  // First try platform_admins
  const { data: platformAdmin } = await supabase
    .from('platform_admins')
    .select('id')
    .eq('id', userId)
    .maybeSingle();

  if (platformAdmin) {
    const { error } = await supabase
      .from('platform_admins')
      .delete()
      .eq('id', userId);

    if (error) throw error;
    return;
  }

  // Then try organization_members
  const { error } = await supabase
    .from('organization_members')
    .delete()
    .eq('id', userId);

  if (error) throw error;
}
